import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { format, addDays, isSameDay } from "date-fns";
import {
  Loader2, ChevronLeft, ChevronRight, MapPin, AlertTriangle, X, Clock, User as UserIcon,
  CheckSquare, UserPlus, Check, Route as RouteIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { loadGoogleMaps, pinIcon } from "../../utils/googleMaps";
import { buildTechColorMap, TECH_PICKER_PALETTE, UNASSIGNED_COLOR } from "../../utils/techColors";
import RoutePanel from "./RoutePanel";
import useJobStatuses from "../../hooks/useJobStatuses";
import { API } from "../../App";

const FALLBACK = "#3b82f6";

// Jobs page Map View — markers for every scheduled job with a usable address.
// Date range + filters live in JobsPage state so they survive modal opens and
// view switches; the map instance lives in a ref so position is preserved
// while the job details modal is open.

// ── Marker hover card (InfoWindow) helpers ──
const escHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const svgIcon = (paths) =>
  `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#8a8a8a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:2px">${paths}</svg>`;
const ICON_PIN = svgIcon('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0Z"/><circle cx="12" cy="10" r="3"/>');
const ICON_CLOCK = svgIcon('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>');
const ICON_USER = svgIcon('<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>');

const hoverWhen = (j) => {
  const date = j.scheduled_date ? format(new Date(j.scheduled_date), "EEE, MMM d") : "";
  const time = (j.scheduled_time || "").trim();
  const end = (j.scheduled_time_end || "").trim();
  const hours = time ? (end ? `${time} – ${end}` : time) : "";
  if (date && hours) return `${date} · ${hours}`;
  return date || hours || "";
};

const hoverRow = (icon, text) =>
  text ? `<div style="display:flex;gap:6px;align-items:flex-start;color:#444;font-size:11.5px;line-height:1.35">${icon}<span>${escHtml(text)}</span></div>` : "";

// "2:00 PM" / "14:00" → minutes since midnight (null when unparseable)
const parseMins = (t) => {
  const m = String(t || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = +m[1];
  const ap = m[3]?.toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + +m[2];
};
const fmtClock = (secs) => {
  const h24 = Math.floor(secs / 3600) % 24;
  const m = Math.floor((secs % 3600) / 60);
  return `${h24 % 12 || 12}:${String(m).padStart(2, "0")} ${h24 >= 12 ? "PM" : "AM"}`;
};

const hoverRouteLine = (r) => {
  if (!r || !r.stop) return "";
  const parts = [`Stop #${r.stop}`];
  if (r.eta_s != null) parts.push(`ETA ${fmtClock(r.eta_s)}`);
  if (r.drive_s) parts.push(`${Math.max(1, Math.round(r.drive_s / 60))} min drive`);
  return `<div style="display:inline-flex;align-items:center;gap:5px;margin-bottom:4px;font-size:10.5px;font-weight:700;color:#111;background:${escHtml(r.color || "#FFCC00")}22;border:1px solid ${escHtml(r.color || "#FFCC00")};border-radius:99px;padding:2px 8px">
    <span style="width:14px;height:14px;border-radius:99px;background:${escHtml(r.color || "#FFCC00")};color:#fff;font-size:9px;font-weight:800;display:inline-flex;align-items:center;justify-content:center">${r.stop}</span>${escHtml(parts.slice(1).join(" · ") || "On route")}
  </div>`;
};

const hoverJobHtml = (j, { statusLabel, statusColor, routeInfo, compact = false }) => {
  const chip = `<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;color:#555;background:#f1f1f1;border-radius:99px;padding:1px 7px;white-space:nowrap"><span style="width:7px;height:7px;border-radius:99px;background:${escHtml(statusColor(j.status))}"></span>${escHtml(statusLabel(j.status))}</span>`;
  return `<div style="padding:${compact ? "7px 0" : "2px 0"};${compact ? "border-top:1px solid #ececec" : ""}">
    ${hoverRouteLine(routeInfo ? routeInfo(j.job_id) : null)}
    <div style="display:flex;justify-content:space-between;gap:8px;align-items:center;margin-bottom:3px">
      <span style="font-weight:700;font-size:12.5px;color:#111">${escHtml(j.customer_name || "No customer")}</span>${chip}
    </div>
    ${j.title ? `<div style="font-size:11.5px;color:#333;margin-bottom:4px">${escHtml(j.title)}</div>` : ""}
    <div style="display:flex;flex-direction:column;gap:3px">
      ${hoverRow(ICON_PIN, j.customer_address)}
      ${hoverRow(ICON_CLOCK, hoverWhen(j))}
      ${hoverRow(ICON_USER, j.assigned_to_name || "Unassigned")}
    </div>
  </div>`;
};

const hoverCardHtml = (groupJobs, helpers) => {
  const MAX = 3;
  const shown = groupJobs.slice(0, MAX);
  const more = groupJobs.length - shown.length;
  return `<div style="font-family:inherit;max-width:270px;min-width:190px;padding:2px 2px 1px">
    ${groupJobs.length > 1 ? `<div style="font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#888;margin-bottom:2px">${groupJobs.length} jobs at this address</div>` : ""}
    ${shown.map((j, i) => hoverJobHtml(j, { ...helpers, compact: i > 0 })).join("")}
    ${more > 0 ? `<div style="font-size:10.5px;color:#777;padding-top:6px;border-top:1px solid #ececec">+${more} more — click the pin to view all</div>` : ""}
  </div>`;
};

const JobsMapView = ({
  jobs, range, setRange, onPreview, getToday, loading, teamMembers = [], onBulkAssigned,
  techFilter = "all", setTechFilter, onTechColorChange,
}) => {
  const [coords, setCoords] = useState({});
  const [geoLoading, setGeoLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [chooser, setChooser] = useState(null); // [{job...}] at one address
  const [rangeMode, setRangeMode] = useState(() => !isSameDay(range.start, range.end));
  // ── Selection mode (bulk technician assignment) ──
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkTech, setBulkTech] = useState("");
  const [assigning, setAssigning] = useState(false);
  // ── Route planner (manual stop ordering) ──
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeTech, setRouteTech] = useState("");
  const [routeData, setRouteData] = useState(null); // {techId, stops, route}
  // ── Legend color editing ──
  const [legendEdit, setLegendEdit] = useState(null); // techId | null
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);
  const hoverIW = useRef(null); // shared hover InfoWindow
  const routeOverlays = useRef([]);
  const fitSigRef = useRef("");
  const { statuses } = useJobStatuses();

  const activeTechs = teamMembers.filter((t) => (t.status || "active") === "active");
  const techColorMap = useMemo(() => buildTechColorMap(teamMembers), [teamMembers]);
  const jobColor = (job) =>
    (job.assigned_to && techColorMap[job.assigned_to]) || UNASSIGNED_COLOR;
  // job_id → stop number for the technician whose route is being planned
  const routeStopMap = useMemo(
    () => Object.fromEntries((routeData?.stops || []).map((s) => [s.job_id, s.stop_number])),
    [routeData]
  );

  // job_id → {stop, eta_s, drive_s} for the planned route. ETAs are anchored on
  // the first stop's scheduled time, then add each job's duration + drive time.
  const routeEtaMap = useMemo(() => {
    const stops = routeData?.stops;
    if (!stops?.length) return {};
    const ordered = [...stops].sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0));
    const legs = routeData.route?.legs || [];
    const jobById = Object.fromEntries(jobs.map((j) => [j.job_id, j]));
    const out = {};
    ordered.forEach((s, i) => {
      out[s.job_id] = { stop: s.stop_number || i + 1, drive_s: i > 0 ? legs[i - 1]?.duration_s || 0 : 0 };
    });
    const firstMins = parseMins(ordered[0]?.scheduled_time || jobById[ordered[0]?.job_id]?.scheduled_time);
    if (firstMins != null) {
      let t = firstMins * 60;
      ordered.forEach((s, i) => {
        if (i > 0) {
          const prev = jobById[ordered[i - 1].job_id];
          const serviceS = Math.round((Number(prev?.duration_hours) > 0 ? Number(prev.duration_hours) : 1) * 3600);
          t += serviceS + (legs[i - 1]?.duration_s || 0);
        }
        out[s.job_id].eta_s = t;
      });
    }
    return out;
  }, [routeData, jobs]);

  const saveTechColor = async (techId, color) => {
    setLegendEdit(null);
    try {
      await axios.patch(`${API}/technicians/${techId}/color`, { color }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      onTechColorChange?.(techId, color);
      toast.success("Technician color updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update color");
    }
  };

  const toggleSelect = (jobId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });

  // Dates changed → clear the whole selection (safety: hidden jobs must never
  // be assigned accidentally).
  useEffect(() => {
    setSelected(new Set());
  }, [range.start, range.end]);

  // Filters/job set changed → prune ids that are no longer visible.
  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const visible = new Set(jobs.map((j) => j.job_id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [jobs]);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
    setChooser(null);
  };

  const bulkAssign = async () => {
    const ids = [...selected];
    if (!ids.length || !bulkTech || assigning) return;
    setAssigning(true);
    try {
      const res = await axios.post(
        `${API}/jobs/bulk-assign`,
        { job_ids: ids, assigned_to: bulkTech },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const ok = res.data.assigned || [];
      const failed = res.data.failed || [];
      if (ok.length) {
        toast.success(`${ok.length} job${ok.length !== 1 ? "s" : ""} assigned to ${res.data.assigned_to_name || "technician"}`);
      }
      if (failed.length) {
        toast.error(`${failed.length} job${failed.length !== 1 ? "s" : ""} couldn't be assigned — still selected so you can retry`);
        setSelected(new Set(failed.map((f) => f.job_id)));
      } else {
        setSelected(new Set());
      }
      onBulkAssigned?.(ok, {
        assigned_to: res.data.assigned_to || "",
        assigned_to_name: res.data.assigned_to_name || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk assignment failed");
    } finally {
      setAssigning(false);
    }
  };

  const statusColor = (id) => statuses.find((s) => s.id === id)?.color || FALLBACK;
  const statusLabel = (id) => statuses.find((s) => s.id === id)?.label || id;

  const today = getToday();
  const shiftDays = (n) =>
    setRange({ start: addDays(range.start, n), end: addDays(range.end, n) });

  // Geocode the visible jobs (cached server-side on each job doc).
  // Keyed on id+address signature so assignment-only updates don't re-fetch.
  const geoSig = jobs.map((j) => `${j.job_id}:${j.customer_address || ""}`).sort().join("|");
  useEffect(() => {
    const ids = jobs.filter((j) => (j.customer_address || "").trim()).map((j) => j.job_id);
    if (!ids.length) { setCoords({}); return; }
    let on = true;
    setGeoLoading(true);
    axios.post(`${API}/jobs/map-geo`, { job_ids: ids }, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => { if (on) setCoords(res.data.coords || {}); })
      .catch(() => { if (on) setCoords({}); })
      .finally(() => { if (on) setGeoLoading(false); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoSig]);

  // Render markers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapRef.current) return;
      let maps;
      try {
        maps = await loadGoogleMaps("token");
      } catch {
        setMapError("Couldn't load Google Maps. Check that the Maps JavaScript API is enabled for the company's Google key.");
        return;
      }
      if (cancelled || !mapRef.current) return;
      if (!mapObj.current) {
        mapObj.current = new maps.Map(mapRef.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
          clickableIcons: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
      }
      const map = mapObj.current;
      if (!hoverIW.current) {
        try {
          hoverIW.current = new maps.InfoWindow({ disableAutoPan: true, headerDisabled: true });
        } catch {
          hoverIW.current = new maps.InfoWindow({ disableAutoPan: true });
        }
      }
      hoverIW.current.close();
      markers.current.forEach((m) => m.setMap(null));
      markers.current = [];

      const mappable = jobs.filter((j) => coords[j.job_id]);
      if (!mappable.length) return;

      // Group jobs sharing the same coordinates so all stay reachable
      const groups = new Map();
      mappable.forEach((j) => {
        const c = coords[j.job_id];
        const key = `${c.lat.toFixed(5)},${c.lng.toFixed(5)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(j);
      });

      const bounds = new maps.LatLngBounds();
      groups.forEach((groupJobs) => {
        const c = coords[groupJobs[0].job_id];
        const multi = groupJobs.length > 1;
        const selCount = selectMode ? groupJobs.filter((j) => selected.has(j.job_id)).length : 0;
        const highlight = selCount > 0;
        const stopNum = !multi ? routeStopMap[groupJobs[0].job_id] : undefined;
        const color = highlight ? "#FFCC00" : jobColor(groupJobs[0]);
        const marker = new maps.Marker({
          position: { lat: c.lat, lng: c.lng },
          map,
          zIndex: highlight ? 999 : stopNum ? 500 : undefined,
          label: multi
            ? {
                text: highlight ? `${selCount}/${groupJobs.length}` : String(groupJobs.length),
                color: highlight ? "#1a1a1a" : "#fff",
                fontSize: highlight ? "11px" : "13px",
                fontWeight: "700",
              }
            : highlight
            ? { text: "✓", color: "#1a1a1a", fontSize: "15px", fontWeight: "800" }
            : stopNum
            ? { text: String(stopNum), color: "#fff", fontSize: "13px", fontWeight: "800" }
            : undefined,
          icon: pinIcon(maps, color, {
            size: multi ? (highlight ? 50 : 46) : highlight ? 46 : stopNum ? 44 : 40,
            withDot: !multi && !highlight && !stopNum,
          }),
        });
        marker.addListener("mouseover", () => {
          const routeColor = routeData?.techId ? techColorMap[routeData.techId] || "#FFCC00" : "#FFCC00";
          const routeInfo = (jobId) => (routeEtaMap[jobId] ? { ...routeEtaMap[jobId], color: routeColor } : null);
          hoverIW.current.setContent(hoverCardHtml(groupJobs, { statusLabel, statusColor, routeInfo }));
          hoverIW.current.open({ map, anchor: marker });
        });
        marker.addListener("mouseout", () => hoverIW.current.close());
        marker.addListener("click", () => {
          hoverIW.current.close();
          if (selectMode) {
            if (groupJobs.length === 1) toggleSelect(groupJobs[0].job_id);
            else setChooser(groupJobs);
          } else if (groupJobs.length === 1) onPreview(groupJobs[0]);
          else setChooser(groupJobs);
        });
        markers.current.push(marker);
        bounds.extend({ lat: c.lat, lng: c.lng });
      });

      // Fit only when the visible job set actually changes — preserves the
      // user's pan/zoom when the details modal opens/closes.
      const sig = [...groups.keys()].sort().join("|");
      if (sig !== fitSigRef.current) {
        fitSigRef.current = sig;
        if (groups.size === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(14);
        } else {
          map.fitBounds(bounds, 56);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords, jobs, statuses, selectMode, selected, techColorMap, routeStopMap, routeEtaMap, routeData]);

  // Draw the planned route polyline in the technician's color
  useEffect(() => {
    const maps = window.google?.maps;
    const map = mapObj.current;
    routeOverlays.current.forEach((o) => o.setMap(null));
    routeOverlays.current = [];
    if (!maps?.geometry || !map || !routeData?.route?.legs) return;
    const color = techColorMap[routeData.techId] || "#FFCC00";
    routeData.route.legs.forEach((leg) => {
      if (!leg.polyline) return;
      routeOverlays.current.push(new maps.Polyline({
        path: maps.geometry.encoding.decodePath(leg.polyline),
        strokeColor: color,
        strokeOpacity: 0.85,
        strokeWeight: 4,
        map,
      }));
    });
    return () => {
      routeOverlays.current.forEach((o) => o.setMap(null));
      routeOverlays.current = [];
    };
  }, [routeData, techColorMap]);

  // Route planning works on a single day — clear it when a range is picked
  useEffect(() => {
    if (!isSameDay(range.start, range.end)) {
      setRouteTech("");
      setRouteData(null);
    }
  }, [range.start, range.end]);

  const unmapped = jobs.filter((j) => !coords[j.job_id]);
  const fmt = (d) => format(d, "yyyy-MM-dd");
  const singleDay = isSameDay(range.start, range.end);

  const rangeLabel = singleDay
    ? format(range.start, "EEEE, MMM d, yyyy")
    : `${format(range.start, "MMM d")} – ${format(range.end, "MMM d, yyyy")}`;

  return (
    <div className="space-y-3" data-testid="jobs-map-view">
      {/* Date controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center bg-theme-surface rounded-lg p-0.5">
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md text-muted-foreground hover:text-foreground"
            onClick={() => shiftDays(-1)} data-testid="jobs-map-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md text-muted-foreground hover:text-foreground"
            onClick={() => shiftDays(1)} data-testid="jobs-map-next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <button
          onClick={() => { setRangeMode(false); setRange({ start: today, end: today }); }}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            singleDay && isSameDay(range.start, today)
              ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
              : "bg-theme-surface text-muted-foreground border-border hover:bg-theme-hover"}`}
          data-testid="jobs-map-today"
        >
          Today
        </button>
        <button
          onClick={() => { setRangeMode(false); const t = addDays(today, 1); setRange({ start: t, end: t }); }}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            singleDay && isSameDay(range.start, addDays(today, 1))
              ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
              : "bg-theme-surface text-muted-foreground border-border hover:bg-theme-hover"}`}
          data-testid="jobs-map-tomorrow"
        >
          Tomorrow
        </button>
        {!rangeMode ? (
          <input
            type="date"
            value={fmt(range.start)}
            onChange={(e) => {
              if (!e.target.value) return;
              const d = new Date(`${e.target.value}T12:00:00`);
              setRange({ start: d, end: d });
            }}
            className="h-7 px-2 rounded-lg border border-border bg-theme-surface text-foreground text-xs"
            data-testid="jobs-map-date-input"
          />
        ) : (
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={fmt(range.start)}
              onChange={(e) => e.target.value && setRange({ ...range, start: new Date(`${e.target.value}T12:00:00`) })}
              className="h-7 px-2 rounded-lg border border-border bg-theme-surface text-foreground text-xs"
              data-testid="jobs-map-range-start"
            />
            <span className="text-xs text-muted-foreground">–</span>
            <input
              type="date"
              value={fmt(range.end)}
              min={fmt(range.start)}
              onChange={(e) => e.target.value && setRange({ ...range, end: new Date(`${e.target.value}T12:00:00`) })}
              className="h-7 px-2 rounded-lg border border-border bg-theme-surface text-foreground text-xs"
              data-testid="jobs-map-range-end"
            />
          </div>
        )}
        <button
          onClick={() => {
            if (rangeMode) { setRangeMode(false); setRange({ start: range.start, end: range.start }); }
            else { setRangeMode(true); setRange({ start: range.start, end: addDays(range.start, 6) }); }
          }}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            rangeMode
              ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
              : "bg-theme-surface text-muted-foreground border-border hover:bg-theme-hover"}`}
          data-testid="jobs-map-range-toggle"
        >
          Date range
        </button>
        <span className="text-xs text-muted-foreground" data-testid="jobs-map-range-label">{rangeLabel}</span>
        <button
          onClick={() => setRouteOpen((v) => !v)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ml-auto ${
            routeOpen || routeData
              ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
              : "bg-theme-surface text-muted-foreground border-border hover:bg-theme-hover"}`}
          data-testid="jobs-map-route-toggle"
        >
          <RouteIcon className="w-3.5 h-3.5" />
          Routes
        </button>
        {!routeOpen && routeData && (
          <span
            className="px-2 py-1 rounded-full text-[11px] font-medium border border-border bg-theme-surface inline-flex items-center gap-1.5"
            data-testid="jobs-map-active-route-chip"
          >
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: techColorMap[routeData.techId] || "#FFCC00" }} />
            <span className="text-foreground/80">
              {activeTechs.find((t) => (t.technician_id || t.member_id) === routeData.techId)?.name || "Route"} · {routeData.stops?.length || 0} stops
            </span>
            <button
              onClick={() => { setRouteTech(""); setRouteData(null); }}
              className="text-muted-foreground hover:text-foreground"
              title="Clear route"
              data-testid="jobs-map-clear-route-btn"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
        <button
          onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${
            selectMode
              ? "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40"
              : "bg-theme-surface text-muted-foreground border-border hover:bg-theme-hover"}`}
          data-testid="jobs-map-select-toggle"
        >
          <CheckSquare className="w-3.5 h-3.5" />
          {selectMode ? "Done" : "Select jobs"}
        </button>
      </div>

      {/* Bulk assignment bar */}
      {selectMode && (
        <div className="rounded-xl border border-[#FFCC00]/40 bg-[#FFCC00]/[0.06] p-3 space-y-2" data-testid="jobs-map-bulk-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-foreground" data-testid="jobs-map-selected-count">
              {selected.size} job{selected.size !== 1 ? "s" : ""} selected
            </span>
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              Tap markers to select or deselect
            </span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                  data-testid="jobs-map-clear-selection"
                >
                  Clear
                </button>
              )}
              <Select value={bulkTech} onValueChange={setBulkTech}>
                <SelectTrigger className="h-8 w-[170px] text-xs input-glass" data-testid="jobs-map-bulk-tech-select">
                  <SelectValue placeholder="Choose technician" />
                </SelectTrigger>
                <SelectContent>
                  {activeTechs.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">No active technicians</div>
                  )}
                  {activeTechs.map((t) => {
                    const id = t.technician_id || t.member_id;
                    return (
                      <SelectItem key={id} value={id} data-testid={`jobs-map-bulk-tech-${id}`}>
                        {t.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="h-8 text-xs bg-[#FFCC00] text-black hover:bg-[#e6b800] font-semibold"
                disabled={!selected.size || !bulkTech || assigning}
                onClick={bulkAssign}
                data-testid="jobs-map-bulk-assign-btn"
              >
                {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />}
                Assign
              </Button>
            </div>
          </div>
          {selected.size > 0 && (
            <div className="flex gap-1.5 flex-wrap" data-testid="jobs-map-selected-chips">
              {jobs.filter((j) => selected.has(j.job_id)).map((j) => (
                <span
                  key={j.job_id}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-theme-surface border border-border text-foreground"
                  data-testid={`jobs-map-selected-chip-${j.job_id}`}
                >
                  {j.customer_name || j.title || j.job_id.slice(0, 6)}
                  <button
                    onClick={() => toggleSelect(j.job_id)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Deselect"
                    data-testid={`jobs-map-deselect-${j.job_id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Technician color legend — click a name to filter, click the dot to recolor */}
      {activeTechs.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="jobs-map-legend">
          <button
            onClick={() => setTechFilter?.(techFilter === "unassigned" ? "all" : "unassigned")}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
              techFilter === "unassigned"
                ? "bg-theme-hover text-foreground border-foreground/30 ring-1 ring-foreground/20"
                : "bg-theme-surface text-muted-foreground border-border hover:text-foreground"}`}
            data-testid="jobs-map-legend-unassigned"
          >
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: UNASSIGNED_COLOR }} />
            Unassigned
          </button>
          {activeTechs.map((t) => {
            const id = t.technician_id || t.member_id;
            const color = techColorMap[id];
            const active = techFilter === id;
            return (
              <div key={id} className="relative">
                <div
                  className={`inline-flex items-center rounded-full border overflow-hidden transition-colors ${
                    active
                      ? "bg-theme-hover border-foreground/30 ring-1 ring-foreground/20"
                      : "bg-theme-surface border-border"}`}
                >
                  <button
                    onClick={() => setLegendEdit(legendEdit === id ? null : id)}
                    className="pl-2 pr-1 py-1 flex items-center"
                    title="Change color"
                    data-testid={`jobs-map-legend-color-${id}`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block hover:ring-1 hover:ring-foreground/40"
                      style={{ background: color }}
                    />
                  </button>
                  <button
                    onClick={() => setTechFilter?.(active ? "all" : id)}
                    className={`pr-2 pl-0.5 py-0.5 text-[11px] ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    title={active ? "Show all technicians" : `Show only ${t.name}'s jobs`}
                    data-testid={`jobs-map-legend-${id}`}
                  >
                    {t.name}
                  </button>
                </div>
                {legendEdit === id && (
                  <div
                    className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-1 p-2 rounded-xl bg-card border border-border shadow-xl w-[172px] max-w-[calc(100vw-16px)]"
                    data-testid="jobs-map-color-picker"
                  >
                    <div className="grid grid-cols-6 gap-1.5">
                      {TECH_PICKER_PALETTE.map((c) => (
                        <button
                          key={c}
                          onClick={() => saveTechColor(id, c)}
                          className={`w-5 h-5 rounded-full border border-black/10 ${color?.toLowerCase() === c.toLowerCase() ? "ring-2 ring-foreground" : ""}`}
                          style={{ background: c }}
                          title={c}
                          data-testid={`jobs-map-color-opt-${c.slice(1).toLowerCase()}`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => saveTechColor(id, null)}
                      className="mt-1.5 w-full text-[11px] text-muted-foreground hover:text-foreground"
                      data-testid="jobs-map-color-reset"
                    >
                      Reset to default
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary row */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1" data-testid="jobs-map-count">
          <MapPin className="w-3.5 h-3.5" />
          {jobs.length - unmapped.length} of {jobs.length} job{jobs.length !== 1 ? "s" : ""} on map
        </span>
        {(loading || geoLoading) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {unmapped.length > 0 && !geoLoading && (
          <span className="flex items-center gap-1 text-amber-500" data-testid="jobs-map-unmapped-count">
            <AlertTriangle className="w-3.5 h-3.5" /> {unmapped.length} without a usable address
          </span>
        )}
      </div>

      {/* Map */}
      <div className="relative rounded-2xl overflow-hidden border border-border/60" style={{ height: "60vh", minHeight: 380 }}>
        <div ref={mapRef} className="absolute inset-0" data-testid="jobs-map-canvas" />
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background z-10 p-6 text-center">
            <p className="text-sm text-muted-foreground max-w-sm" data-testid="jobs-map-error">{mapError}</p>
          </div>
        )}
        {!mapError && !loading && !geoLoading && jobs.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 z-10 p-6 text-center">
            <p className="text-sm text-muted-foreground" data-testid="jobs-map-empty">No jobs match the selected dates and filters</p>
          </div>
        )}

        {/* Same-address chooser */}
        {chooser && (
          <div className="absolute left-2 right-2 bottom-2 z-20 rounded-xl bg-card border border-border shadow-xl max-h-[55%] overflow-y-auto" data-testid="jobs-map-chooser">
            <div className="flex items-center justify-between px-3 pt-2 pb-1 sticky top-0 bg-card">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {chooser.length} jobs at this address
              </span>
              <button onClick={() => setChooser(null)} className="text-muted-foreground p-1" data-testid="jobs-map-chooser-close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {chooser.map((j) => (
              <button
                key={j.job_id}
                onClick={() => {
                  if (selectMode) toggleSelect(j.job_id);
                  else { setChooser(null); onPreview(j); }
                }}
                className="w-full text-left px-3 py-2.5 border-t border-border/40 hover:bg-muted/40"
                data-testid={`jobs-map-chooser-${j.job_id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {selectMode && (
                    <span
                      className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                        selected.has(j.job_id)
                          ? "bg-[#FFCC00] border-[#FFCC00]"
                          : "border-border bg-theme-surface"
                      }`}
                      data-testid={`jobs-map-chooser-check-${j.job_id}`}
                    >
                      {selected.has(j.job_id) && <Check className="w-3 h-3 text-black" />}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-foreground">{j.customer_name || "No name"}</span>
                  <Badge className="text-[10px] px-1.5 border" style={{ background: `${statusColor(j.status)}15`, color: statusColor(j.status), borderColor: `${statusColor(j.status)}30` }}>
                    {statusLabel(j.status)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{j.title}</p>
                <p className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />
                    {j.scheduled_date ? format(new Date(j.scheduled_date), "MMM d") : "—"}{j.scheduled_time ? ` · ${j.scheduled_time}` : ""}
                  </span>
                  {j.assigned_to_name && <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" />{j.assigned_to_name}</span>}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Route planner — pick a tech + arrange their day's stop sequence */}
      {routeOpen && (
        singleDay ? (
          <RoutePanel
            techs={activeTechs}
            techColorMap={techColorMap}
            date={range.start}
            selectedTech={routeTech}
            setSelectedTech={setRouteTech}
            onRouteData={setRouteData}
            refreshKey={jobs}
          />
        ) : (
          <div className="rounded-xl border border-border bg-card p-3" data-testid="jobs-map-route-single-day-hint">
            <p className="text-xs text-muted-foreground">
              Route planning works one day at a time — switch off "Date range" or pick a single day to arrange a technician's stops.
            </p>
          </div>
        )
      )}

      {/* Jobs without a usable address */}
      {unmapped.length > 0 && !geoLoading && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="jobs-map-unmapped-list">
          <p className="text-xs font-semibold text-amber-500 mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Not shown on the map
          </p>
          {unmapped.slice(0, 15).map((j) => (
            <div key={j.job_id} className="flex items-center justify-between gap-2 py-1">
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{j.customer_name || "No name"} — {j.title}</p>
                <p className="text-[11px] text-muted-foreground">
                  {(j.customer_address || "").trim() ? "Address couldn't be located" : "No address on the job"}
                </p>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs flex-shrink-0 border-border"
                onClick={() => onPreview(j)} data-testid={`jobs-map-unmapped-open-${j.job_id}`}>
                Open
              </Button>
            </div>
          ))}
          {unmapped.length > 15 && (
            <p className="text-[11px] text-muted-foreground mt-1">…and {unmapped.length - 15} more</p>
          )}
        </div>
      )}
    </div>
  );
};

export default JobsMapView;
